import React, { useRef } from "react";
import { GroupedRoute } from "./types/transitTypes";
import { generateInstructions } from "./routeInstructions";

// Props for route card rendering and navigation
interface RouteCardProps {
  groupedRoutes: GroupedRoute[]; // Array of grouped route data
  currentRouteIndex: number; // Index of the selected route
  onRouteIndexChange: (index: number) => void; // Callback to update route index
}

// Component to render route cards with navigation
const RouteCard: React.FC<RouteCardProps> = ({
  groupedRoutes,
  currentRouteIndex,
  onRouteIndexChange,
}) => {
  // Refs for touch navigation
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  // Handle touch navigation for route cards
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      const threshold = 50;
      if (Math.abs(diff) > threshold) {
        if (diff > 0 && groupedRoutes[currentRouteIndex + 1]) {
          onRouteIndexChange(currentRouteIndex + 1);
        } else if (diff < 0 && currentRouteIndex > 0) {
          onRouteIndexChange(currentRouteIndex - 1);
        }
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Handle mouse navigation for route cards
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    touchStartX.current = e.clientX;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (touchStartX.current !== null) {
      touchEndX.current = e.clientX;
    }
  };

  const handleMouseUp = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      const threshold = 50;
      if (Math.abs(diff) > threshold) {
        if (diff > 0 && groupedRoutes[currentRouteIndex + 1]) {
          onRouteIndexChange(currentRouteIndex + 1);
        } else if (diff < 0 && currentRouteIndex > 0) {
          onRouteIndexChange(currentRouteIndex - 1);
        }
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Render route cards or placeholder
  return (
    <div className="route-cards-container">
      {groupedRoutes?.length > 0 ? (
        <>
          {/* Navigation buttons */}
          <div className="route-navigation">
            <button
              onClick={() => onRouteIndexChange(currentRouteIndex - 1)}
              disabled={currentRouteIndex === 0}
              data-testid="prev-route"
            >
              ← Previous
            </button>
            <span>
              Option {currentRouteIndex + 1} of {groupedRoutes.length}
            </span>
            <button
              onClick={() => onRouteIndexChange(currentRouteIndex + 1)}
              disabled={currentRouteIndex === groupedRoutes.length - 1}
              data-testid="next-route"
            >
              Next →
            </button>
          </div>
          {/* Route card with instructions */}
          <div
            className="route-card"
            data-testid="route-card"
            key={currentRouteIndex}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <h3>Route {currentRouteIndex + 1}</h3>
            <div
              style={{ padding: "10px" }}
              dangerouslySetInnerHTML={{
                __html: generateInstructions(
                  groupedRoutes[currentRouteIndex] || {}
                ).join("<br /><br />"),
              }}
            />
          </div>
        </>
      ) : (
        /* Placeholder when no routes */
        <div className="route-card" data-testid="route-card">
          <h3>Route Information</h3>
          <div style={{ padding: "10px" }}>
            Instructions will show when routes are found.
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteCard;
